import { ArrowLeft, Clock, ImageOff, Merge, Pencil, Search, Send, ShoppingCart, User, Wallet, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import CartItemCard from "@/components/cart/CartItemCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePizzaPopup } from "@/components/pizza/usePizzaPopup";
import ProductPopupCardapio from "@/components/ProductPopupCardapio";
import { AvisoOfertasCliente, useOfertasNoCarrinho } from "@/features/ofertas-do-dia";
import { temPersonalizacao } from "@/utils/cartGrouping";
import { imprimirCupomPreparo } from "@/utils/cupomPreparo";
import { CarregandoGrade } from "@/components/carregando";
import { miniaturaDe } from "@/utils/miniatura";

import { comandasApi, erroDe } from "./api";
import { CancelarItemDialog, JuntarDialog, TransferirDialog } from "./Dialogos";
import ItensLancados from "./ItensLancados";
import { itemDoCarrinhoParaPedido, itensParaPreparo, reais, tempoAberta, totalDoCarrinho, vivos } from "./lib";

const MODULO = "Comandas";

const GRADE_PRODUTOS = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2";

function CartaoProduto({ p, onClick }) {
  const esgotado = p.available === false;
  return (
    <button type="button" onClick={onClick} disabled={esgotado} data-e2e="comanda-produto"
      className={`text-left rounded-xl border bg-card overflow-hidden transition-all ${esgotado ? "cursor-not-allowed" : "hover:shadow-md hover:border-primary/40 active:scale-[0.98]"}`}>
      <div className="relative aspect-square bg-muted/40 grid place-items-center overflow-hidden">
        {p.photo_url ? <img src={miniaturaDe(p.photo_url, 256)} alt="" loading="lazy" className="w-full h-full object-cover" />
          : <ImageOff className="w-7 h-7 text-muted-foreground/40" />}
        {/* Selo de esgotado igual ao do cardápio (dono, 25/10/2026) — antes o cartão só ficava apagado */}
        {esgotado && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-e2e="comanda-produto-esgotado">
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-red-500 text-white shadow-sm flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Esgotado
            </span>
          </div>
        )}
      </div>
      <div className="p-2">
        <p className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2rem]">{p.name}</p>
        <p className={`text-sm font-bold mt-1 ${esgotado ? "text-muted-foreground" : "text-orange-500"}`}>{reais(p.sale_price)}{p.sale_unit && p.sale_unit !== "un" ? <span className="text-[10px] text-muted-foreground font-normal">/{p.sale_unit}</span> : null}</p>
      </div>
    </button>
  );
}

/**
 * A comanda aberta: à esquerda o cardápio (busca, categorias, produtos), à
 * direita o carrinho do que ainda não foi enviado, os itens já lançados e as
 * ações (editar, juntar, fechar; cancelar fica nos ⋮ do cartão da lista). Reusa o popup de produto, o de
 * pizza, as ofertas do cliente e o cupom de preparo de Mesas.
 */
export default function ComandaAberta({ comanda, produtos, categorias, allProducts, activePromotions, outras, darkMode,
  imprimePreparo = true, carregandoProdutos = false, onVoltar, onAtualizar, onEditar, onFechar, onSair }) {
  const pedido = comanda?.pedido;
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editando, setEditando] = useState(null); // {cartItemId, initial}
  const [enviando, setEnviando] = useState(false);
  const [cancelarItem, setCancelarItem] = useState(null); // {idx, it}
  const [transferirItem, setTransferirItem] = useState(null);
  const [juntarAberto, setJuntarAberto] = useState(false);

  useEffect(() => { setCart([]); }, [comanda?.id]);

  // Teclado dentro da comanda (dono, 26/10/2026):
  // - Enter minimiza: volta para a lista; com itens no carrinho, lança antes.
  // - Espaço: Fechar comanda (abre o pagamento).
  // - Esc: volta para a lista; com itens no carrinho, pergunta antes de largar.
  // Com janela aberta (popup do produto, pagamento…) a tecla é dela; digitando
  // na busca, Enter e Espaço são do campo.
  const [confirmarVoltar, setConfirmarVoltar] = useState(false);
  const atalhos = useRef(null);
  useEffect(() => {
    const tecla = (e) => {
      if (e.defaultPrevented || e.repeat || e.ctrlKey || e.altKey || e.metaKey) return;
      if (document.querySelector("[role=dialog], [role=alertdialog]")) return;
      const a = atalhos.current;
      if (!a) return;
      if (e.key === "Escape") {
        if (a.cart.length) setConfirmarVoltar(true);
        else a.onVoltar();
        return;
      }
      if (e.target?.closest?.("input, textarea, select, [contenteditable=true]")) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (a.cart.length) a.enviar().then((ok) => ok && a.onVoltar());
        else a.onVoltar();
      } else if (e.key === " ") {
        e.preventDefault();
        if (a.cart.length) toast.warning("Envie (Enter) ou tire os itens do carrinho antes de fechar a comanda");
        else if (!a.itensVivos.length) toast.warning("A comanda não tem itens para fechar");
        else a.onFechar();
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  const ofertasCliente = useOfertasNoCarrinho({ clienteId: comanda?.cliente_id || null, modulo: MODULO, cart, setCart, produtos: allProducts });
  const adicionar = (data) => {
    const toAdd = { ...data, sale_price: data.final_price ?? data.sale_price ?? 0, cartItemId: Date.now() };
    setCart((atual) => {
      if (editando) return atual.map((i) => (i.cartItemId === editando.cartItemId ? { ...toAdd, cartItemId: editando.cartItemId } : i));
      if (temPersonalizacao(data)) return [...atual, toAdd];
      const igual = atual.find((i) => i.id === data.id && !temPersonalizacao(i));
      if (igual) return atual.map((i) => (i === igual ? { ...i, quantity: (i.quantity || 1) + (data.quantity || 1) } : i));
      return [...atual, toAdd];
    });
    setEditando(null);
  };
  const pizza = usePizzaPopup({
    clienteId: ofertasCliente?.estado === "ativo" ? comanda?.cliente_id : null,
    moduloOferta: MODULO,
    onAddToCart: (item) => adicionar({ ...item, final_price: item.preco || item.sale_price || 0, sale_price: item.preco || item.sale_price || 0, quantity: item.quantity || 1 }),
    allProducts, darkMode: !!darkMode,
  });

  const abrirProduto = (p) => {
    if (pizza.tryOpenPizza(p)) return;
    setSelectedProduct(p); setPopupOpen(true);
  };
  const fecharPopup = () => { setPopupOpen(false); setSelectedProduct(null); setEditando(null); };
  const editarDoCarrinho = (item) => {
    const prod = (allProducts || []).find((p) => p.id === item.id) || item;
    setEditando({ cartItemId: item.cartItemId, initial: { quantity: item.quantity, observation: item.observation, combo_type: item.combo_type, step_selections: item.step_selections } });
    setSelectedProduct(prod); setPopupOpen(true);
  };
  const mudarQtd = (cartItemId, delta) => setCart((atual) => atual.map((i) => {
    if (i.cartItemId !== cartItemId) return i;
    const passo = i.is_divisible ? (["kg", "l"].includes(String(i.sale_unit || "").toLowerCase()) ? 0.1 : 0.5) : 1;
    const nova = Math.round(((i.quantity || 1) + delta * passo) * 1000) / 1000;
    return nova <= 0 ? i : { ...i, quantity: nova };
  }));

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (produtos || []).filter((p) => (categoria === "todas" || (p.category || "Sem categoria") === categoria)
      && (!termo || String(p.name || "").toLowerCase().includes(termo)));
  }, [produtos, categoria, busca]);
  const nomesCategorias = useMemo(() => {
    const presentes = new Set((produtos || []).map((p) => p.category || "Sem categoria"));
    const ordem = (categorias || []).map((c) => c.name).filter((n) => presentes.has(n));
    presentes.forEach((n) => { if (!ordem.includes(n)) ordem.push(n); });
    return ordem;
  }, [produtos, categorias]);

  // Devolve true quando lançou (o Enter usa para minimizar depois)
  const enviar = async () => {
    if (!cart.length || enviando) return false;
    setEnviando(true);
    const items = cart.map((i) => itemDoCarrinhoParaPedido(i, allProducts));
    try {
      const detalhe = await comandasApi.lancar(comanda.id, items, totalDoCarrinho(cart));
      setCart([]);
      onAtualizar(detalhe);
      toast.success(`${items.length} ${items.length === 1 ? "item lançado" : "itens lançados"} na comanda ${String(comanda.numero).padStart(2, "0")}`);
      if (imprimePreparo) {
        imprimirCupomPreparo({
          items: itensParaPreparo(items, allProducts), codigo: detalhe?.pedido?.codigo, created_at: new Date().toISOString(),
          cliente_nome: comanda.cliente_nome || "—", comanda: String(comanda.numero).padStart(2, "0"), modulo: MODULO, _tituloItens: "— NOVOS ITENS —",
        }).catch(() => {});
      }
      return true;
    } catch (e) {
      toast.error(erroDe(e, "Não foi possível lançar"));
      return false;
    } finally {
      setEnviando(false);
    }
  };

  const itensVivos = vivos(pedido?.items);
  atalhos.current = { cart, enviar, onVoltar, onFechar, itensVivos };
  const totalComanda = comanda?.total || 0;
  const nn = String(comanda?.numero || 0).padStart(2, "0");

  return (
    <div className="p-3 sm:p-4" data-e2e="comanda-aberta">
      <div className="max-w-7xl mx-auto grid gap-4 lg:grid-cols-[minmax(0,1fr)_400px] items-start">
        {/* Cardápio */}
        <div className="space-y-3 min-w-0">
          <div className="flex items-center gap-3">
            <button type="button" onClick={onVoltar} className="h-10 w-10 grid place-items-center rounded-lg border hover:bg-muted shrink-0" aria-label="Voltar" title="Voltar às comandas (Enter ou Esc)"><ArrowLeft className="w-5 h-5" /></button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold leading-tight">Comanda {nn} <span className="font-normal text-muted-foreground">{comanda.cliente_nome ? `· ${comanda.cliente_nome}` : ""}</span></h1>
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" /> aberta há {tempoAberta(comanda.opened_at)}{comanda.aberta_por?.nome ? ` · por ${comanda.aberta_por.nome}` : ""}</p>
            </div>
            <button type="button" onClick={onEditar} className="h-10 px-3 rounded-lg border text-sm font-semibold hover:bg-muted inline-flex items-center gap-1.5" data-e2e="comanda-editar"><Pencil className="w-4 h-4" /> <span className="hidden sm:inline">Cliente</span></button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto…" data-e2e="comanda-busca-produto"
              className="w-full h-10 pl-9 pr-3 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {["todas", ...nomesCategorias].map((n) => (
              <button key={n} type="button" onClick={() => setCategoria(n)}
                className={`h-8 px-3 rounded-full text-xs font-semibold border shrink-0 ${categoria === n ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:border-primary/50"}`}>
                {n === "todas" ? "Todas" : n}
              </button>
            ))}
          </div>
          {/* Cardápio ainda chegando (1ª abertura): cards com foto em cinza, não "Nenhum produto" */}
          {carregandoProdutos ? <CarregandoGrade comImagem quantos={10} className={GRADE_PRODUTOS} /> : (
            <>
              {!lista.length && <p className="text-sm text-muted-foreground py-8 text-center">Nenhum produto à venda em Comandas{busca ? ` com “${busca}”` : ""}. Confira os Canais de Venda do produto.</p>}
              <div className={GRADE_PRODUTOS}>
                {lista.map((p) => <CartaoProduto key={p.id} p={p} onClick={() => abrirProduto(p)} />)}
              </div>
            </>
          )}
        </div>

        {/* Carrinho, itens e ações */}
        <aside className="lg:sticky lg:top-3 space-y-3">
          <section className="rounded-2xl border bg-card overflow-hidden">
            <header className="px-4 py-2.5 border-b flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-orange-500" />
              <h2 className="font-bold text-sm">Para enviar</h2>
              {cart.length > 0 && <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white">{cart.length}</span>}
            </header>
            <div className="p-2 max-h-[40vh] overflow-y-auto">
              <AvisoOfertasCliente ofertasDia={ofertasCliente} cart={cart} className="mb-2" />
              {!cart.length && <p className="text-xs text-muted-foreground text-center py-4">Toque num produto para adicionar.</p>}
              <div className="space-y-2">
                {cart.map((item) => (
                  <CartItemCard key={item.cartItemId} item={item} allProducts={allProducts}
                    onIncrement={() => mudarQtd(item.cartItemId, 1)} onDecrement={() => mudarQtd(item.cartItemId, -1)}
                    onRemove={() => setCart((a) => a.filter((i) => i.cartItemId !== item.cartItemId))} onEdit={() => editarDoCarrinho(item)} />
                ))}
              </div>
            </div>
            {cart.length > 0 && (
              <div className="p-3 border-t flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Novos: <b className="text-foreground">{reais(totalDoCarrinho(cart))}</b></span>
                <button type="button" onClick={enviar} disabled={enviando} data-e2e="comanda-enviar"
                  className="ml-auto h-10 px-5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                  <Send className="w-4 h-4" /> {enviando ? "Enviando…" : "Enviar"}
                  <kbd className="hidden sm:inline text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/20">Enter</kbd>
                </button>
              </div>
            )}
          </section>

          <section className="rounded-2xl border bg-card overflow-hidden" data-e2e="comanda-lancados">
            <header className="px-4 py-2.5 border-b flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-bold text-sm">Na comanda</h2>
              <span className="ml-auto text-sm font-bold text-orange-500 tabular-nums" data-e2e="comanda-total">{reais(totalComanda)}</span>
            </header>
            <div className="px-4 max-h-[36vh] overflow-y-auto">
              <ItensLancados items={pedido?.items} allProducts={allProducts?.length ? allProducts : produtos} onCancelar={(idx, it) => setCancelarItem({ idx, it })} onTransferir={(idx, it) => setTransferirItem({ idx, it })} />
            </div>
            <div className="p-3 border-t grid gap-2">
              <button type="button" onClick={() => setJuntarAberto(true)} disabled={!outras?.length} className="h-10 rounded-lg border text-sm font-semibold hover:bg-muted inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-e2e="comanda-juntar-abrir">
                <Merge className="w-4 h-4" /> Juntar
              </button>
              <button type="button" onClick={onFechar} disabled={!itensVivos.length || cart.length > 0} data-e2e="comanda-fechar"
                title={cart.length ? "Envie ou tire os itens do carrinho antes de fechar" : undefined}
                className="h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                <Wallet className="w-5 h-5" /> Fechar comanda · {reais(totalComanda)}
                <kbd className="hidden sm:inline text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white/20">Espaço</kbd>
              </button>
            </div>
          </section>
        </aside>
      </div>

      <AlertDialog open={confirmarVoltar} onOpenChange={setConfirmarVoltar}>
        <AlertDialogContent data-e2e="comanda-voltar-confirmar">
          <AlertDialogHeader>
            <AlertDialogTitle>Voltar sem lançar?</AlertDialogTitle>
            <AlertDialogDescription>
              {cart.length} {cart.length === 1 ? "item no carrinho ainda não foi lançado" : "itens no carrinho ainda não foram lançados"} na comanda e {cart.length === 1 ? "será descartado" : "serão descartados"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar na comanda</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmarVoltar(false); onVoltar(); }}>Voltar às comandas</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProductPopupCardapio product={selectedProduct} open={popupOpen} onClose={fecharPopup} onAddToCart={adicionar} darkMode={!!darkMode}
        allProducts={allProducts?.length ? allProducts : produtos} activePromotions={activePromotions || []} initialData={editando?.initial || null} modulo={MODULO} />
      {pizza.popup}

      <CancelarItemDialog item={cancelarItem?.it} open={!!cancelarItem} onClose={() => setCancelarItem(null)}
        onConfirmar={async (quantidade, motivo) => {
          try { onAtualizar(await comandasApi.cancelarItem(comanda.id, cancelarItem.idx, quantidade, motivo)); toast.success("Item cancelado"); }
          catch (e) { toast.error(erroDe(e, "Não foi possível cancelar")); throw e; }
        }} />
      <TransferirDialog item={transferirItem?.it} outras={outras} open={!!transferirItem} onClose={() => setTransferirItem(null)}
        onConfirmar={async (destino, quantidade) => {
          try { const r = await comandasApi.transferir(comanda.id, transferirItem.idx, quantidade, destino); onAtualizar(r.origem); toast.success("Item transferido"); }
          catch (e) { toast.error(erroDe(e, "Não foi possível transferir")); throw e; }
        }} />
      <JuntarDialog comanda={comanda} outras={outras} open={juntarAberto} onClose={() => setJuntarAberto(false)}
        onConfirmar={async (ids) => {
          try { onAtualizar(await comandasApi.juntar(comanda.id, ids)); toast.success(`${ids.length} ${ids.length === 1 ? "comanda juntada" : "comandas juntadas"}`); }
          catch (e) { toast.error(erroDe(e, "Não foi possível juntar")); throw e; }
        }} />
    </div>
  );
}
